import Select from '@components/v2/form/Select/Select';
import Alert from '@components/v2/ui/Alert/Alert';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import LangEnum from '../../../lang/LangEnum';
import { useTranslation } from '../../translation/TranslationContext';
import { AccessConfig, ShareChannelEnum } from '../types/sharedEntities.enum';
import { IShareOrderContent, ISharedEntityForm } from '../types/sharedEntities.types';
import CatalogAutocomplete from './CatalogAutocomplete';
import ChannelButton from './ChannelButton';
import EmailAutocomplete from './EmailAutocomplete';

const ShareOrderContent = ({ entityId, disabledConfigAccess }: IShareOrderContent) => {
  const { translate } = useTranslation();
  const { control, setValue } = useFormContext<ISharedEntityForm>();

  const channelType = useWatch({ name: 'config.channelType', control });
  const accessConfig = useWatch({ name: 'accessConfig', control });
  const isPrivate = accessConfig === AccessConfig.Private;
  const isEmail = channelType === ShareChannelEnum.EMAIL;
  const isWhatsapp = channelType === ShareChannelEnum.WHATSAPP;

  const accessConfigOptions = [
    { value: AccessConfig.Private, label: translate(LangEnum.SHARE_ORDER_PRIVATE_ACCESS) },
    { value: AccessConfig.Public, label: translate(LangEnum.SHARE_ORDER_PUBLIC_ACCESS) },
  ];

  return (
    <Stack sx={{ gap: 3 }}>
      {!isPrivate && (
        <Alert severity="warning" message={LangEnum.SHARE_ORDER_PUBLIC_ACCESS_WARNING} />
      )}
      <Stack sx={{ gap: 1 }}>
        <Controller
          control={control}
          name="accessConfig"
          render={({ field }) => (
            <Select
              label={translate(LangEnum.GLOBAL_CONFIG_ACCESS)}
              value={field.value}
              options={accessConfigOptions}
              disabled={disabledConfigAccess}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
        <Typography color="text.secondary">
          {translate(
            isPrivate
              ? LangEnum.PRIVATE_SUGGESTED_SHARE_ORDER_INFO
              : LangEnum.PUBLIC_SUGGESTED_SHARE_ORDER_INFO,
          )}
        </Typography>
      </Stack>

      {!isPrivate && (
        <Stack sx={{ gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {translate(LangEnum.SHARE_ORDER_LINK_CATALOG)}
          </Typography>
          <Controller
            control={control}
            name="config.catalog"
            render={({ field }) => (
              <CatalogAutocomplete
                onChange={field.onChange}
                disabledListPick={!entityId}
                filter={{ isPublicLink: true }}
              />
            )}
          />
        </Stack>
      )}

      <Stack sx={{ gap: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {translate(LangEnum.GLOBAL_SHARE_BY)}
        </Typography>
        <Stack direction="row" sx={{ gap: 1 }}>
          <ChannelButton
            label={LangEnum.GLOBAL_WHATSAPP}
            isActive={isWhatsapp}
            handleClick={() => setValue('config.channelType', ShareChannelEnum.WHATSAPP)}
          />
          <ChannelButton
            label={LangEnum.GLOBAL_EMAIL}
            isActive={isEmail}
            handleClick={() => setValue('config.channelType', ShareChannelEnum.EMAIL)}
          />
        </Stack>
        {isEmail && (
          <Controller
            control={control}
            name="config.emails"
            render={({ field }) => <EmailAutocomplete field={field} entityId={entityId} />}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default ShareOrderContent;
